/**
 * @file     PropertyCard.tsx
 * @readme   ./CLAUDE.md
 * @purpose  Property listing card. Discriminated union accepts curated PropertyListing rows, MLS card rows and owner listings (rendered by OwnerListingCard), normalizes them through cardAdapters.toDisplay, and renders two presentations: the boxed card (map search, saved, dashboard) with its action rows, and the public hub card (4:3 photo, one glass status badge, glass Watch and Compare over the photo, tight body, pinned BCRES attribution, whole card links). Attribution is full and unclamped (MLS only).
 */
'use client';

import '@/core/i18n/deferred/rentalsSearch';
import '@/core/i18n/deferred/rentalsHubs';
import Image from 'next/image';
import { reltaTrack } from '@/hooks/useReltaTrack';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import { Fragment, useState } from 'react';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import { fadeInOnLoad, photoZoom } from '@/core/motion';
import { heroGradient, glassBlur, semanticRoleColors } from '@/design-system/tokens';
import { tint } from '@/design-system/tokens/tint';
import { layoutTokens } from '@/design-system/tokens/layout';
import { hubRadius } from '@/components/hub/hubKitStyles';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useCardCta } from '../CardCtaContext';
import { useInListingModal } from '../listingModalScope';
import { CardNavPending } from './CardNavPending';
import { useHydratedNow } from '@/hooks/useHydratedNow';
import { applyPhotoFallback } from '@/services/mls/mlsImageLoader';
import type { PropertyListing } from '@/core/types/property';
import { ThumbsUpIcon } from '@/components/icons/ThumbsUpIcon';
import { interceptInPageListingClick } from './inPageListingClick';
import { useCardSaveCompare } from './useCardSaveCompare';
import {
  AddToListButton,
  CARD_NARROW_QUERY,
  CardAttribution,
  CardIconActions,
  PhotoCountPill,
  PhotoScrim,
  PillDot,
  RemovedFromListSnackbar,
  WatchedSnackbar,
  glassPillSx,
  statusBadgePlacementSx,
  stretchedLinkSx,
} from './cardChrome';
import { HoverPhotoGallery } from './HoverPhotoGallery';
import { VowLockedPriceChange } from '@/components/vow/VowLockedPriceChange';
import { SuggestToClientControl, useSuggestToClient } from '../SuggestToClient';
import { cardActionButtonSx } from './cardActionButtonSx';
import { OwnerListingCard } from './OwnerListingCard';
import { AvailabilityBadge, FairRentChip, RentPrice, RentalFeatureIcons } from './rentalCardParts';
import { DEFAULT_ASSUMPTIONS, monthlyPayment } from '@/services/rent/rentMortgage';
import {
  toDisplay,
  resolvePropertyCardViewAction,
  resolveAgentCtaState,
  type PropertyCardProps,
  type MlsOrCuratedCardProps,
  type MlsListingCardData,
} from '../../services/cardAdapters';

export type { PropertyCardProps, MlsListingCardData };

/**
 * `card` (default) is the boxed card used by search, saved and the dashboard.
 * `bare` is the hub grid card from the kit: no box, a photo with the base
 * radius, then text. Content, actions and compliance chrome are identical.
 */
export type PropertyCardPresentation = 'card' | 'bare';

/** Hub grid: 2 columns on phone, 3 from md, 4 from lg. */
const HUB_GRID_IMAGE_SIZES = '(max-width: 899px) 50vw, (max-width: 1199px) 33vw, 320px';

const formatPrice = (price: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(price);

// Monthly mortgage payment. Defaults to 20% down, 25yr amort, 5.19% (5yr fixed
// posted); the rent-vs-mortgage family overrides these via mortgageAssumptions.
// Display only — not financial advice. Assumptions shown in tooltip disclaimer.

/** Shape shared by the three presale pills. */
const presalePillSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '5px',
  height: 22,
  px: '9px',
  borderRadius: 999,
  border: '1px solid',
  fontSize: 11.5,
  lineHeight: 1,
  whiteSpace: 'nowrap',
} as const;

const formatMonthly = (n: number) =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);


const STATUS_I18N: Record<PropertyListing['status'], { key: string; defaultValue: string }> = {
  active: { key: 'properties.card.status.active', defaultValue: 'Active' },
  pending: { key: 'properties.card.status.pending', defaultValue: 'Pending' },
  sold: { key: 'properties.card.status.sold', defaultValue: 'Sold' },
  presale: { key: 'properties.card.status.presale', defaultValue: 'Presale' },
};

const STATUS_FALLBACK = { key: 'properties.card.status.active', defaultValue: 'Active' };

const TYPE_I18N: Record<PropertyListing['type'], { key: string; defaultValue: string }> = {
  detached: { key: 'properties.card.type.detached', defaultValue: 'Detached' },
  'semi-detached': { key: 'properties.card.type.semiDetached', defaultValue: 'Semi-detached' },
  townhouse: { key: 'properties.card.type.townhouse', defaultValue: 'Townhouse' },
  condo: { key: 'properties.card.type.condo', defaultValue: 'Condo' },
};

const TYPE_FALLBACK = { key: 'properties.card.type.condo', defaultValue: 'Condo' };

// Marks an agent action already filed for this buyer (shortlisted / sent).
const CheckIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

type PresentationProps = {
  presentation?: PropertyCardPresentation;
  /** Bare card on a surface whose wrapper opens the listing on card click
   *  (the map panel): drop the redundant view button from the action row. */
  hideViewAction?: boolean;
  /** The photo's `sizes`, for grids whose columns differ from the hub grid's. */
  imageSizes?: string;
};

/** Owner listings take their own boxed variant; every other source renders the listing card. */
export const PropertyCard = (props: PropertyCardProps & PresentationProps) =>
  props.source === 'owner' ? <OwnerListingCard {...props} /> : <ListingCardBody {...props} />;

const ListingCardBody = (props: MlsOrCuratedCardProps & PresentationProps) => {
  const { theme } = useAppTheme();
  const { trackCtaClick } = useAnalytics();
  const { t } = useTranslation();

  const locationTag = props.locationTag ?? 'property_card';
  const bare = props.presentation === 'bare';
  const bodyPx = `${theme.layout.cardPadding.md}px`;
  const now = useHydratedNow();
  const display = toDisplay(props, { now });
  // Listing detail renders live, so prefetching every visible card cost a full
  // server render per card, including for JS-running bots. A same-tab card that
  // really navigates warms the detail page only on a person's hover or focus;
  // inside ListingModalLinks the click opens the modal, and new-tab cards never
  // use a prefetch. Touch is left out so scrolling a phone grid warms nothing;
  // CardNavPending shows feedback on the tap instead.
  const inListingModal = useInListingModal();
  const [warm, setWarm] = useState(false);
  const warmable = Boolean(props.openInSameTab) && !inListingModal && !props.disableLinkNavigation;
  const cardPrefetch = warmable ? warm : false;
  const warmOnIntent = warmable && !warm
    ? { onMouseEnter: () => setWarm(true), onFocus: () => setWarm(true) }
    : {};
  // VOW off-market treatment: status chip + informational card (no detail
  // route, no watch/compare). vowGated marks a decoy card for visitors who
  // haven't passed the VOW gate — photo/price/address blur, click unlocks.
  const offMarketStatus = display.offMarketStatus ?? null;
  const vowGated = display.vowGated === true;
  // MLS® rental: rent per month, availability and rental icons; no mortgage,
  // price cut, open house or sold language.
  const lease = display.listingType === 'lease';
  const listingAnalytics = { listing_type: display.listingType ?? 'sale', listing_source: display.listingSource ?? props.source };
  const priority = 'priority' in props ? (props.priority ?? false) : false;
  const hideLikeButton = props.hideLikeButton ?? false;
  const showMortgage = (props.showMortgage ?? false) && !lease;
  const mortgageAssumptions = props.mortgageAssumptions ?? DEFAULT_ASSUMPTIONS;
  const rentComparison = props.rentComparison ?? null;
  const onViewDetails = props.onViewDetails;
  const agentActions = props.agentActions;
  const agentListingKey =
    props.source === 'mls' ? props.listing.listingKey : (props.listing.slug ?? '');
  const onInquire = props.onInquire;
  // Surface-level CTA, supplied by CardCtaProvider. Null everywhere it is not
  // wired, which keeps this default-off. Yields to onInquire: the liked list
  // and the shortlist review modal already own that button.
  const cardCta = useCardCta();
  // The hub grid shows an alert/link CTA once in its header (GridCtaButton),
  // not on every card. Inquiry CTAs are listing-scoped, so they stay here.
  const showCardCta =
    cardCta != null && !onInquire && !offMarketStatus && (!bare || cardCta.kind === 'inquiry');
  const isInquiryCta = Boolean(showCardCta && cardCta?.kind === 'inquiry');
  const viewVariant = showCardCta && !isInquiryCta ? 'contained' : 'outlined';
  const monthly = showMortgage && display.price != null ? monthlyPayment(display.price, mortgageAssumptions) : null;
  const estRent =
    props.source === 'mls' && props.showRentEstimate && props.listing.estimatedMonthlyRent != null
      ? props.listing.estimatedMonthlyRent
      : null;
  // "Under your rent": the visitor's rent (set only by the rent-vs-mortgage
  // family) covers this listing's mortgage payment. Shares the top-right slot
  // with the price-cut chip; price-cut wins deterministically so they can't
  // collide (README §PropertyCard note).
  const underRent =
    rentComparison != null && monthly != null && !display.priceCut && monthly <= rentComparison;
  const hoverTransition = `${theme.motion.durations.normal}ms ${theme.motion.easing.standard}`;
  const viewDetailsLabel = props.viewDetailsLabel ?? ((onInquire || showCardCta) && !offMarketStatus
    ? t('properties.card.view', { defaultValue: 'View' })
    : t('properties.card.viewDetails', { defaultValue: 'View Details' }));
  const viewDetailsSx = {
    flexGrow: (onInquire || isInquiryCta) && !offMarketStatus ? 0 : 1,
    flexShrink: 0,
    textTransform: 'none',
    fontWeight: 500,
    fontSize: `${theme.layout.button.sm.fontSize}px`,
    px: `${theme.layout.button.sm.paddingX}px`,
    py: `${theme.layout.button.sm.paddingY}px`,
    minHeight: `${theme.layout.button.sm.minHeight}px`,
    borderRadius: `${theme.layout.radius.md}px`,
  } as const;
  // Hub grid actions: small icon + label text buttons on one line, 44px
  // touch height, no outlined boxes.
  const bareActionSx = (active = false) => ({
    flex: '0 0 auto',
    minWidth: 0,
    minHeight: 44,
    px: 1,
    py: 0,
    border: 'none',
    borderRadius: `${theme.layout.radius.sm}px`,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: 13.5,
    whiteSpace: 'nowrap',
    color: active ? theme.semanticColors.interactivePrimary : theme.semanticColors.textSecondary,
    '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
    '&:hover': {
      color: theme.semanticColors.interactivePrimary,
      backgroundColor: theme.semanticColors.interactiveHover,
      boxShadow: 'none',
      border: 'none',
    },
    '&.Mui-disabled': { color: theme.semanticColors.textMuted, backgroundColor: 'transparent' },
    '&:focus-visible': {
      outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`,
      outlineOffset: 0,
    },
  } as const);
  const viewAction = resolvePropertyCardViewAction({
    href: display.href,
    hasViewHandler: onViewDetails != null,
    openInSameTab: props.openInSameTab === true,
    offMarket: offMarketStatus != null && !vowGated,
    vowGated,
    inPageNavigation: props.disableLinkNavigation === true,
  });

  // Curated-only state
  const curatedListing = props.source === 'curated' ? props.listing : null;

  const suggest = useSuggestToClient();
  const showSuggest =
    suggest.available &&
    Boolean(agentListingKey) &&
    !vowGated &&
    !agentActions;
  const stackSecondaryActions = showSuggest && !hideLikeButton;
  const compareSlug = props.source === 'mls' ? props.listing.listingKey : props.listing.slug;
  const {
    isWishlisted,
    onWatch: handleWishlist,
    isInCompare,
    canAddToCompare,
    onCompare: handleCompare,
    snackOpen,
    closeSnack,
    removedOpen,
    closeRemoved,
    undoRemove,
  } = useCardSaveCompare({
    source: props.source,
    id: compareSlug,
    title: display.title,
    price: display.price,
    thumbSrc: display.primaryImageSrc,
    href: display.href,
    locationTag,
    gatedHandler: vowGated ? () => onViewDetails?.() : null,
    listingType: display.listingType === 'lease' ? 'lease' : 'sale',
  });

  // Curated-only status badge color
  const statusColor =
    curatedListing == null
      ? theme.rawSemanticColors.textMuted
      : curatedListing.status === 'active'
        ? theme.rawSemanticColors.statusSuccess
        : curatedListing.status === 'pending'
          ? theme.rawSemanticColors.statusWarning
          : theme.rawSemanticColors.textMuted;

  // Resolved property-type label (curated uses i18n map; MLS shows raw string)
  const typeLabel =
    curatedListing != null
      ? t((TYPE_I18N[curatedListing.type] ?? TYPE_FALLBACK).key, {
          defaultValue: (TYPE_I18N[curatedListing.type] ?? TYPE_FALLBACK).defaultValue,
        })
      : display.propertyType;

  const subtitleLine = curatedListing
    ? `${typeLabel} · ${display.subtitle}`
    : [typeLabel, display.subtitle].filter(Boolean).join(' · ');

  const sqftUnit = t('properties.card.sqft', { defaultValue: 'sqft' });
  const openHouseText = display.nextOpenHouseLabel
    ? display.nextOpenHouseLabel.replace(/^Open House(?: on)?\s+/i, 'Open ')
    : null;
  // Unit separator: the shared formatter writes "#2303 – 4815 Eldorado Mews".
  // An en dash is for numeric ranges only (voice and tone), so the card shows
  // "#2303, 4815 Eldorado Mews".
  const displayTitle = display.title.replace(/^(#\S+)\s+–\s+/, '$1, ');
  const builtText =
    display.isMls && display.yearBuilt && !display.isPresale
      ? `${t('properties.card.built', { defaultValue: 'Built' })} ${display.yearBuilt}`
      : display.isMls && offMarketStatus
        ? t('properties.card.builtNa', { defaultValue: 'Built n/a' })
        : null;
  const specParts: { key: string; text: string; blur?: boolean }[] = [
    display.beds != null ? { key: 'bd', text: `${display.beds} ${t('properties.card.bd', { defaultValue: 'bd' })}` } : null,
    display.baths != null ? { key: 'ba', text: `${display.baths} ${t('properties.card.ba', { defaultValue: 'ba' })}` } : null,
    display.sqft != null ? { key: 'sqft', text: `${display.sqft.toLocaleString('en-CA')} ${sqftUnit}` } : null,
    builtText ? { key: 'built', text: builtText, blur: true } : null,
  ].filter((part): part is { key: string; text: string; blur?: boolean } => part != null);

  // Asking-price history only (D13): the drop from this listing's own peak
  // ask. The old price and the size of the cut are VOW data (owner,
  // 2026-10-10): only a VOW registrant's payload carries pct and from, and
  // the chip adds the amount (or the percentage when the current price is
  // unknown). Everyone else gets "Price reduced" and a blurred placeholder
  // button into the VOW gate.
  const priceCut = display.priceCut;
  const priceCutAmount =
    priceCut?.from != null && display.price != null ? Math.round(priceCut.from - display.price) : null;
  const priceCutFigure =
    priceCutAmount != null && priceCutAmount > 0
      ? formatPrice(priceCutAmount)
      : priceCut?.pct != null
        ? `${priceCut.pct}%`
        : null;
  const showPriceCut = Boolean(display.isMls && priceCut && !offMarketStatus);
  // Figures are appended, not interpolated, so SSR before i18n init still
  // prints the amount.
  const priceCutPrefix = t('properties.card.priceReducedPrefix', { defaultValue: 'Price reduced' });
  const priceCutContent = showPriceCut ? (
    <>
      <Box component="span" aria-hidden sx={{ display: 'inline-flex', color: theme.semanticColors.statusSuccess }}>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M6 13l6 6 6-6" />
        </svg>
      </Box>
      {priceCutFigure ? (
        `${priceCutPrefix} ${priceCutFigure}`
      ) : (
        <>
          {priceCutPrefix}
          <VowLockedPriceChange listingKey={props.source === 'mls' ? props.listing.listingKey : undefined} surface="card" />
        </>
      )}
    </>
  ) : null;

  /* Presale pills (brief 08): assignment, completion as the listing
      states it, GST included. The slot keeps its height whenever the
      row is a presale so grid cards stay aligned. Status colours sit
      behind the text, never on it: warning and success text at this
      size fail 4.5:1 in light mode. */
  const presalePills = display.isPresale ? (
      <Stack
        direction="row"
        flexWrap="wrap"
        data-testid="presale-pills"
        sx={{ gap: 1, minHeight: 22, pt: 0.25 }}
      >
        {display.isAssignment ? (
          <Tooltip
            title={t('properties.card.presale.assignmentTip', { defaultValue: 'You take over a purchase contract from a person, not the developer.' })}
            placement="top"
            enterTouchDelay={0}
          >
            <Box component="span" sx={{ ...presalePillSx, fontWeight: 600, color: theme.semanticColors.textPrimary, backgroundColor: tint(theme.semanticColors.statusWarning, 16), borderColor: tint(theme.semanticColors.statusWarning, 60) }}>
              <Box component="span" aria-hidden sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: theme.semanticColors.statusWarning, flexShrink: 0 }} />
              {t('properties.card.presale.assignment', { defaultValue: 'Assignment' })}
            </Box>
          </Tooltip>
        ) : null}
        {display.completionEstimate ? (
          <Tooltip
            title={t('properties.card.presale.completionTip', { defaultValue: 'As stated in the listing' })}
            placement="top"
            enterTouchDelay={0}
          >
            <Box component="span" sx={{ ...presalePillSx, fontWeight: 500, color: theme.semanticColors.textSecondary, backgroundColor: theme.semanticColors.surfaceRecessed, borderColor: theme.semanticColors.borderSubtle }}>
              <Box component="span" sx={{ display: 'inline-flex', '& svg': { width: 12, height: 12 } }}><ClockIcon /></Box>
              {t('properties.card.presale.completion', { defaultValue: 'Est. {{when}}', when: display.completionEstimate })}
            </Box>
          </Tooltip>
        ) : null}
        {display.gstIncluded === true ? (
          <Box component="span" sx={{ ...presalePillSx, fontWeight: 500, color: theme.semanticColors.textPrimary, backgroundColor: tint(theme.semanticColors.statusSuccess, 12), borderColor: tint(theme.semanticColors.statusSuccess, 45) }}>
            <Box component="span" sx={{ display: 'inline-flex', color: theme.semanticColors.statusSuccess, '& svg': { width: 12, height: 12 } }}><CheckIcon /></Box>
            {t('properties.card.presale.gstIncluded', { defaultValue: 'GST included' })}
          </Box>
        ) : null}
      </Stack>
    ) : null;

  const renderSpecs = (parts: typeof specParts) => (
    <>
      {/* Specs rows: beds, baths, sqft and year built on one line that
          wraps between facts, never inside one. Land cards omit dwelling
          specs entirely (toDisplay nulls them) so a vacant lot does not
          paint 0 bd / 0 ba / 0 sqft next to house cards. Off-market cards
          always carry the year-built slot so they match active cards. */}
      {parts.length > 0 ? (
        <Typography
          component="p"
          data-testid="card-specs"
          sx={{
            m: 0,
            fontSize: { xs: 13, md: theme.typography.bodySm },
            lineHeight: 1.45,
            color: theme.semanticColors.textSecondary,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {parts.map((part, i) => (
            <Fragment key={part.key}>
              {i > 0 ? ' · ' : null}
              <Box
                component="span"
                aria-hidden={(part.blur && vowGated) || undefined}
                sx={{
                  whiteSpace: 'nowrap',
                  ...(part.blur && vowGated ? { filter: 'blur(6px)', userSelect: 'none' } : null),
                }}
              >
                {part.text}
              </Box>
            </Fragment>
          ))}
        </Typography>
      ) : null}
    </>
  );

  const agentRow = agentActions ? (
          <>
          {(agentActions.getFootprint?.(agentListingKey) ?? []).length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ pt: `${theme.layout.form.labelGap}px`, flexWrap: 'wrap' }}>
              {agentActions.getFootprint!(agentListingKey).map((label) => (
                <Box
                  key={label}
                  sx={{
                    fontSize: 9,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    px: 0.6,
                    py: 0.2,
                    borderRadius: '4px',
                    border: `1px solid ${theme.semanticColors.borderSubtle}`,
                    color: label === 'LIKED' || label === 'INQUIRED'
                      ? theme.semanticColors.statusError
                      : label === 'VIEWED'
                        ? theme.semanticColors.statusInfo
                        : theme.semanticColors.textSecondary,
                  }}
                >
                  {label}
                </Box>
              ))}
            </Stack>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{ pt: `${theme.layout.form.labelGap}px`, alignItems: 'center' }}
          >
            {([
              {
                key: 'add',
                label: t('properties.card.agentAddToSearch', { defaultValue: 'Add to search' }),
                onClick: () => agentActions.onAddToSearch({ listingKey: agentListingKey, title: display.title }),
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="10.5" cy="10.5" r="6.5" />
                    <line x1="15.5" y1="15.5" x2="20" y2="20" />
                    <line x1="10.5" y1="8" x2="10.5" y2="13" />
                    <line x1="8" y1="10.5" x2="13" y2="10.5" />
                  </svg>
                ),
              },
              {
                key: 'suggest',
                label: t('properties.card.agentSuggest', { defaultValue: 'Suggest' }),
                onClick: () => agentActions.onSuggest({ listingKey: agentListingKey, title: display.title }),
                icon: (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 2L11 13" />
                    <path d="M22 2l-7 20-4-9-9-4 20-7z" />
                  </svg>
                ),
              },
            ] as const).map((btn) => {
              // An action already filed for this buyer reads as done instead of
              // offering itself again, so the agent can see at a glance what
              // they have already sent or added.
              const state = resolveAgentCtaState(
                agentActions.getFootprint?.(agentListingKey) ?? [],
                btn.key,
              );
              // A pending pick is filed but the buyer cannot see it until the
              // shortlist is sent, so it reads amber rather than terminal green.
              const toneColor = state.tone === 'done'
                ? theme.semanticColors.statusSuccess
                : state.tone === 'pending'
                  ? theme.semanticColors.statusWarning
                  : theme.semanticColors.textSecondary;
              return (
                <Button
                  key={btn.key}
                  disabled={state.done}
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); btn.onClick(); }}
                  startIcon={state.tone === 'done' ? <CheckIcon /> : state.tone === 'pending' ? <ClockIcon /> : btn.icon}
                  sx={{
                    flexGrow: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: `${theme.layout.button.sm.fontSize}px`,
                    px: `${theme.layout.button.sm.paddingX}px`,
                    py: `${theme.layout.button.sm.paddingY}px`,
                    minHeight: `${theme.layout.button.sm.minHeight}px`,
                    border: `1px solid ${state.tone === 'idle' ? theme.semanticColors.borderSubtle : toneColor}`,
                    borderRadius: `${theme.layout.radius.md}px`,
                    color: toneColor,
                    transition: 'color 150ms, border-color 150ms',
                    '&.Mui-disabled': {
                      color: toneColor,
                      borderColor: toneColor,
                    },
                    '&:hover': {
                      color: theme.semanticColors.interactivePrimary,
                      borderColor: theme.semanticColors.interactivePrimary,
                    },
                  }}
                >
                  {state.done ? state.label : btn.label}
                </Button>
              );
            })}
          </Stack>
          </>
  ) : null;

  if (bare) {
    const photoRadius = hubRadius.panel;
    const listingKeyAttr = props.source === 'mls' ? props.listing.listingKey : undefined;
    // "Residential" is the feed's umbrella class, so it says nothing; the
    // sub-type (Townhouse, Apartment/Condo) does. The place line only renders
    // when the type adds information.
    const subType = props.source === 'mls' ? props.listing.propertySubType?.trim() || null : null;
    const typeDetail = curatedListing
      ? typeLabel
      : subType ?? (display.propertyType && !/^residential$/i.test(display.propertyType.trim()) ? display.propertyType : null);
    const placeLine = typeDetail ? [display.subtitle, typeDetail].filter(Boolean).join(' · ') : null;
    // One status badge at most. Status facts first (sold, for rent), then
    // the time-boxed reasons to look: open house, new, price drop.
    const offMarketLabel = offMarketStatus === 'Closed'
      ? t('properties.card.offMarketChip.sold', { defaultValue: 'Sold' })
      : offMarketStatus === 'Pending'
        ? t('properties.card.offMarketChip.pending', { defaultValue: 'Pending' })
        : offMarketStatus === 'Expired'
          ? t('properties.card.offMarketChip.expired', { defaultValue: 'Expired' })
          : offMarketStatus
            ? t('properties.card.offMarketChip.terminated', { defaultValue: 'Terminated' })
            : null;
    const curatedStatus = curatedListing
      ? t((STATUS_I18N[curatedListing.status] ?? STATUS_FALLBACK).key, {
          defaultValue: (STATUS_I18N[curatedListing.status] ?? STATUS_FALLBACK).defaultValue,
        })
      : null;
    const badge: { key: string; label: string; dot: string } | null =
      display.isMls && offMarketLabel
        ? { key: 'off-market', label: offMarketLabel, dot: offMarketStatus === 'Closed' ? theme.semanticColors.interactivePrimary : theme.semanticColors.textMuted }
        : display.isRental
          ? { key: 'rent', label: t('properties.card.forRentChip', { defaultValue: 'For Rent' }), dot: theme.semanticColors.interactivePrimary }
          : display.isMls && openHouseText
            ? { key: 'open-house', label: t('properties.card.openHouseChip', { defaultValue: 'Open house' }), dot: semanticRoleColors.link }
            : display.isMls && display.freshnessLabel
              ? {
                  key: 'fresh',
                  label: display.freshnessLabel === 'New'
                    ? t('properties.card.newChip', { defaultValue: 'New' })
                    : t('properties.card.newlyListedChip', { defaultValue: 'Newly Listed' }),
                  dot: theme.semanticColors.interactivePrimary,
                }
              : showPriceCut || curatedListing?.previousPrice
                ? { key: 'price-drop', label: t('properties.card.priceDropChip', { defaultValue: 'Price drop' }), dot: theme.semanticColors.statusSuccess }
                : underRent
                  ? { key: 'under-rent', label: t('properties.card.underRentChip', { defaultValue: 'Under your rent' }), dot: theme.semanticColors.statusSuccess }
                  : curatedStatus
                    ? { key: 'status', label: curatedStatus, dot: statusColor }
                    : display.isMls && display.matchDisclosureLabel
                      ? { key: 'match', label: display.matchDisclosureLabel, dot: theme.semanticColors.textMuted }
                      : null;
    const showIconActions = !offMarketStatus || offMarketStatus === 'Pending';
    const showAddToList = showIconActions && !hideLikeButton;
    const hasAttribution = display.isMls && Boolean(display.attribution || display.mlsId);
    const hasPhotoCount = display.isMls && (display.photosCount ?? 0) > 1;
    const bareSpecs = specParts.filter((part) => part.key !== 'built');
    const showViewAction = !props.hideViewAction && viewAction.kind !== 'link';
    const hasActionRow =
      showCardCta || (onInquire && !offMarketStatus) || showViewAction || showSuggest ||
      agentActions != null || (offMarketStatus != null && offMarketStatus !== 'Pending');

    return (
      <>
      <Box
        component="article"
        data-presentation="bare"
        sx={{
          position: 'relative',
          isolation: 'isolate',
          // Lets the price row respond to the card's own width, not the viewport.
          containerType: 'inline-size',
          containerName: 'listing-card',
          height: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          cursor: 'pointer',
          '& [data-card-photo] img': {
            transition: `${photoZoom.transition}, opacity ${hoverTransition}`,
          },
          '& [data-testid="card-photo-count"]': { transition: `opacity ${hoverTransition}` },
          '@media (hover: hover)': {
            '&:hover [data-card-photo] img': { transform: photoZoom.transform },
            // The gallery dots take over from the count while the card is hovered.
            '&:hover [data-testid="card-photo-count"]': { opacity: 0 },
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& [data-card-photo] img': { transition: 'none' },
            '&:hover [data-card-photo] img': { transform: 'none' },
            '& [data-testid="card-photo-count"]': { transition: 'none' },
          },
        }}
      >
        <Box
          component={Link}
          href={display.href}
          // Lets ListingModalLinks upgrade a plain click into an in-place
          // detail modal on static pages, without giving up the real href.
          data-listing-key={listingKeyAttr}
          prefetch={cardPrefetch}
          {...warmOnIntent}
          target={props.disableLinkNavigation ? undefined : (props.openInSameTab ? undefined : '_blank')}
          rel={props.disableLinkNavigation ? undefined : (props.openInSameTab ? undefined : 'noopener noreferrer')}
          sx={stretchedLinkSx(theme, photoRadius)}
          onClick={(e: React.MouseEvent) => {
            if (props.disableLinkNavigation && interceptInPageListingClick(e)) {
              onViewDetails?.();
            }
            trackCtaClick('View Property', locationTag, display.href, listingAnalytics);
          }}
        >
          <Box
            data-card-photo
            sx={{
              position: 'relative',
              zIndex: 2,
              aspectRatio: '4 / 3',
              background: heroGradient.cardOverlay(alpha, theme.rawColors.primaryMuted, theme.rawColors.secondary),
              borderRadius: `${photoRadius}px`,
              overflow: 'hidden',
              // Keeps the rounded clip on Safari while the photo scales.
              isolation: 'isolate',
            }}
          >
            {display.primaryImageSrc ? (
              display.isMls && !display.offMarketStatus ? (
                <HoverPhotoGallery
                  listingKey={props.source === 'mls' ? props.listing.listingKey : ''}
                  primarySrc={display.primaryImageSrc}
                  alt={display.title}
                  priority={priority}
                  sizes={props.imageSizes ?? HUB_GRID_IMAGE_SIZES}
                />
              ) : (
                <Image
                  src={display.primaryImageSrc}
                  alt={display.title}
                  fill
                  quality={75}
                  sizes={props.imageSizes ?? HUB_GRID_IMAGE_SIZES}
                  // VOW photos are a signed proxy path with a query string,
                  // which the Next image optimizer rejects (400).
                  unoptimized={display.primaryImageSrc.includes('?')}
                  style={{ objectFit: 'cover' }}
                  ref={priority ? undefined : fadeInOnLoad}
                  onError={(e) => applyPhotoFallback(e.currentTarget as HTMLImageElement)}
                />
              )
            ) : (
              <Box aria-hidden sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.3 }}>
                <svg viewBox="0 0 48 48" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.4">
                  <rect x="4" y="16" width="40" height="28" rx="2" />
                  <path d="M4 22L24 8L44 22" />
                  <rect x="18" y="30" width="12" height="14" />
                </svg>
              </Box>
            )}

            {display.primaryImageSrc ? <PhotoScrim /> : null}

            <CardNavPending />

            {/* VOW blur veil over DECOY pixels only (the server never ships
                real VOW bytes to non-registrants). */}
            {vowGated ? (
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  backdropFilter: glassBlur.medium,
                  backgroundColor: tint(theme.semanticColors.surfaceCard, 25),
                }}
              />
            ) : null}

            {lease ? (
              <AvailabilityBadge availableOn={display.availableOn} now={now} hasPhotoCount={hasPhotoCount} />
            ) : badge ? (
              <Box
                data-testid="card-status-badge"
                data-badge={badge.key}
                sx={{
                  ...glassPillSx(theme),
                  ...statusBadgePlacementSx(hasPhotoCount),
                  // The count hides on a phone card, so the badge may use the width.
                  ...(hasPhotoCount ? { maxWidth: { xs: 'calc(100% - 20px)', sm: 'calc(100% - 80px)' } } : null),
                }}
              >
                <PillDot color={badge.dot} />
                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{badge.label}</Box>
              </Box>
            ) : null}

            {/* On a 2-column phone card the badge and the count share the
                bottom edge; the badge wins so its label is never clipped. */}
            {hasPhotoCount ? (
              <Box sx={{ display: badge ? { xs: 'none', sm: 'contents' } : 'contents' }}>
                <PhotoCountPill count={display.photosCount ?? 0} />
              </Box>
            ) : null}
          </Box>

          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', columnGap: 1, flexWrap: 'wrap', minWidth: 0 }}>
              {lease ? <RentPrice amount={display.price} /> : <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                <Typography
                  component="p"
                  aria-hidden={vowGated || undefined}
                  sx={{
                    m: 0,
                    fontSize: { xs: 18, sm: 20 },
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                    fontVariantNumeric: 'tabular-nums',
                    color: theme.semanticColors.textPrimary,
                    whiteSpace: 'nowrap',
                    ...(vowGated ? { filter: 'blur(8px)', userSelect: 'none' } : null),
                  }}
                >
                  {display.price != null ? formatPrice(display.price) : '—'}
                </Typography>
                {curatedListing?.previousPrice ? (
                  <Typography sx={{ fontSize: 12.5, color: theme.semanticColors.textMuted, textDecoration: 'line-through' }}>
                    {formatPrice(curatedListing.previousPrice)}
                  </Typography>
                ) : null}
              </Box>}
              {display.pricePerSqft != null ? (
                <Typography
                  data-testid="card-price-per-sqft"
                  aria-hidden={vowGated || undefined}
                  sx={{
                    fontSize: 12,
                    color: theme.semanticColors.textMuted,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    ...(vowGated ? { filter: 'blur(6px)', userSelect: 'none' } : null),
                    // A 2-column phone card has no room beside a seven-figure
                    // price; the row never wraps, so $/sqft steps aside.
                    [CARD_NARROW_QUERY]: { display: 'none' },
                  }}
                >
                  {formatPrice(display.pricePerSqft)}/{sqftUnit}
                </Typography>
              ) : null}
            </Box>

            {renderSpecs(bareSpecs)}

            {lease ? <RentalFeatureIcons features={display.rentalFeatures} /> : null}

            <Typography
              title={vowGated ? undefined : displayTitle}
              aria-hidden={vowGated || undefined}
              sx={{
                ...(vowGated ? { filter: 'blur(6px)', userSelect: 'none' } : null),
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.35,
                color: theme.semanticColors.textPrimary,
                // The street address alone is clamped so a long one does not
                // push a card taller than its siblings. Attribution never is.
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {displayTitle}
            </Typography>

            {placeLine ? (
              <Typography
                data-testid="card-place"
                title={placeLine}
                sx={{
                  fontSize: 12.5,
                  lineHeight: 1.4,
                  color: theme.semanticColors.textMuted,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minWidth: 0,
                }}
              >
                {placeLine}
              </Typography>
            ) : null}

            {display.isMls && openHouseText ? (
              <Typography
                data-testid="card-open-house"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: 12.5,
                  fontWeight: 500,
                  lineHeight: 1.4,
                  color: semanticRoleColors.link,
                }}
              >
                <Box component="span" aria-hidden sx={{ display: 'inline-flex', flexShrink: 0, '& svg': { width: 13, height: 13 } }}>
                  <ClockIcon />
                </Box>
                {openHouseText}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {/* Outside the link: the price-cut chip holds a button, and a control
            nested in an anchor is a nested-interactive violation. The link's
            stretched overlay still covers the rest of this text. */}
        {lease && props.fairRent ? (
          <Box sx={{ display: 'flex', mt: 0.5, position: 'relative', zIndex: 2 }}>
            <FairRentChip band={props.fairRent} />
          </Box>
        ) : null}

        {showPriceCut || monthly != null || estRent != null || presalePills ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5, minWidth: 0 }}>
            {priceCutContent ? (
              <Box
                component="span"
                data-testid="price-cut-chip"
                sx={{
                  position: 'relative',
                  zIndex: 2,
                  alignSelf: 'flex-start',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minHeight: 24,
                  px: 1,
                  borderRadius: `${layoutTokens.radius.pill}px`,
                  backgroundColor: semanticRoleColors.successSoft,
                  color: theme.semanticColors.textPrimary,
                  fontSize: 12,
                  fontWeight: 500,
                  lineHeight: 1.3,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {priceCutContent}
              </Box>
            ) : null}

            {monthly != null ? (
              <Typography sx={{ fontSize: 12, color: theme.semanticColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                ~{formatMonthly(monthly)}/mo
              </Typography>
            ) : null}

            {estRent != null ? (
              <Typography sx={{ fontSize: 12, color: theme.semanticColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                {t('rentalsHubs.card.estRent', { amount: formatMonthly(estRent) })}
              </Typography>
            ) : null}

            {presalePills}
          </Box>
        ) : null}

        {showIconActions && !hideLikeButton ? (
          <CardIconActions
            watched={isWishlisted}
            onWatch={handleWishlist}
            inCompare={isInCompare}
            canCompare={canAddToCompare}
            onCompare={handleCompare}
          />
        ) : showIconActions ? (
          <CardIconActions
            showWatch={false}
            watched={false}
            onWatch={handleWishlist}
            inCompare={isInCompare}
            canCompare={canAddToCompare}
            onCompare={handleCompare}
          />
        ) : null}

        {display.isMls && (display.attribution || display.mlsId) ? (
          <CardAttribution brokerage={display.attribution} mlsId={display.mlsId} />
        ) : offMarketStatus ? (
          // Keeps the attribution slot's height on off-market cards whose
          // brokerage is absent so they align with their neighbours.
          <Box aria-hidden sx={{ mt: 'auto', pt: 2, fontSize: 11.5, lineHeight: 1.45 }}>{' '}</Box>
        ) : null}

        {hasActionRow ? (
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 0.5,
              mx: -1,
              pt: 0.5,
              ...(display.isMls && (display.attribution || display.mlsId || offMarketStatus) ? null : { mt: 'auto' }),
            }}
          >
            {showCardCta && cardCta ? (
              <Button
                disabled={cardCta.onActivate == null}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  cardCta.onActivate?.({
                    listingKey: agentListingKey,
                    listingSlug: props.source === 'mls' ? (props.listing.listingSlug ?? null) : props.listing.slug,
                    title: display.title,
                    price: display.price,
                    mlsId: display.mlsId ?? null,
                    source: props.source,
                  });
                }}
                sx={bareActionSx(true)}
              >
                {cardCta.label}
              </Button>
            ) : null}
            {onInquire && !offMarketStatus ? (
              <Button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onInquire(); }} sx={bareActionSx(true)}>
                {t('properties.card.askAbout', { defaultValue: 'Inquire' })}
              </Button>
            ) : null}
            {offMarketStatus && offMarketStatus !== 'Pending' ? (
              <Button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onViewDetails?.(); }} sx={bareActionSx(true)}>
                {vowGated
                  ? t('properties.card.vowUnlock', { defaultValue: 'See sold data' })
                  : t('properties.card.soldData', { defaultValue: 'Sold data' })}
              </Button>
            ) : !showViewAction ? null : viewAction.kind === 'callback' ? (
              <Button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onViewDetails?.(); }} sx={bareActionSx(true)}>
                {viewDetailsLabel}
              </Button>
            ) : viewAction.kind === 'disabled' ? (
              <Button disabled sx={bareActionSx()}>{viewDetailsLabel}</Button>
            ) : null}
            {showSuggest ? (
              <SuggestToClientControl variant="button" listing={{ listingKey: agentListingKey, title: display.title }} />
            ) : null}
            {agentActions ? agentRow : null}
          </Box>
        ) : null}

        {showAddToList ? (
          // Last in the card so it pins to the bottom edge across a grid row.
          <Box sx={hasAttribution || offMarketStatus || hasActionRow ? undefined : { mt: 'auto' }}>
            <AddToListButton onList={isWishlisted} onToggle={handleWishlist} />
          </Box>
        ) : null}
      </Box>
      <WatchedSnackbar open={snackOpen} onClose={closeSnack} />
      <RemovedFromListSnackbar open={removedOpen} onClose={closeRemoved} onUndo={undoRemove} />
      </>
    );
  }

  return (
    <>
    <Paper
      component="article"
      elevation={0}
      data-presentation="card"
      sx={{
        backgroundColor: theme.semanticColors.surfaceCard,
        border: `1px solid ${theme.semanticColors.borderSubtle}`,
        borderRadius: `${theme.layout.radius.sm}px`,
        overflow: 'hidden',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: `transform ${hoverTransition}, box-shadow ${hoverTransition}, border-color ${theme.motion.durations.fast}ms ${theme.motion.easing.standard}`,
        '&:hover': {
          transform: `translateY(-${theme.motion.interaction.hoverLift}px)`,
          boxShadow: `0 8px 24px -8px ${alpha(theme.rawSemanticColors.textPrimary, 0.14)}`,
          borderColor: theme.semanticColors.borderStrong,
        },
        '&:focus-within': {
          outline: `2px solid ${theme.semanticColors.interactiveFocusRing}`,
          outlineOffset: 2,
        },
      }}
    >
      {/* When disableLinkNavigation is set (map view: clicking the card
          must open the in-page slide-over instead of navigating), we
          intercept the Link click with preventDefault. The Link still
          renders for accessibility (keyboard users, screen readers,
          right-click "open in new tab") but the default click navigation
          is suppressed; the parent's onClick handler on the wrapper Box
          runs the slide-over open. Telemetry runs in both modes. */}
      <Link
        href={display.href}
        // Lets ListingModalLinks upgrade a plain click into an in-place detail
        // modal on static pages, without giving up the real href.
        data-listing-key={props.source === 'mls' ? props.listing.listingKey : undefined}
        prefetch={cardPrefetch}
        {...warmOnIntent}
        target={props.disableLinkNavigation ? undefined : (props.openInSameTab ? undefined : '_blank')}
        rel={props.disableLinkNavigation ? undefined : (props.openInSameTab ? undefined : 'noopener noreferrer')}
        style={{ textDecoration: 'none', color: 'inherit', display: 'flex', flexDirection: 'column' }}
        onClick={(e) => {
          if (props.disableLinkNavigation) {
            // Allow modified-click ("open in new tab") to keep working —
            // those bypass our slide-over and should follow the href.
            if (!interceptInPageListingClick(e)) {
              trackCtaClick('View Property', locationTag, display.href);
              return;
            }
            onViewDetails?.();
          }
          trackCtaClick('View Property', locationTag, display.href);
        }}
      >
        {/* Image area */}
        <Box
          data-card-photo
          sx={{
            position: 'relative',
            aspectRatio: '16 / 9',
            background: heroGradient.cardOverlay(alpha, theme.rawColors.primaryMuted, theme.rawColors.secondary),
            overflow: 'hidden',
            // Keeps the rounded clip on Safari while the gallery transforms.
            isolation: 'isolate',
          }}
        >
          {display.primaryImageSrc ? (
            display.isMls && !display.offMarketStatus ? (
              <HoverPhotoGallery
                listingKey={props.source === 'mls' ? props.listing.listingKey : ''}
                primarySrc={display.primaryImageSrc}
                alt={display.title}
                priority={priority}
              />
            ) : (
              <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                <Image
                  src={display.primaryImageSrc}
                  alt={display.title}
                  fill
                  quality={75}
                  // Tightened so mobile pulls the 640w bucket (not 750w) and
                  // typography-scale viewport gets a smaller asset. Lighthouse
                  // flagged ~100KB savings per card on /properties mobile from
                  // right-sizing. q=85 → q=75 is visually indistinguishable
                  // on these JPEG cards but saves another ~15% on bytes.
                  sizes={'(max-width: 480px) 95vw, (max-width: 768px) 48vw, (max-width: 1200px) 32vw, 400px'}
                  // VOW photos are a signed proxy path carrying a query string
                  // (?u=&e=&s=). The Next image optimizer rejects a local src
                  // with a query string outright (400), so every off-market
                  // card rendered a broken image. The proxy already returns a
                  // right-sized WebP, so skip the optimizer for those.
                  unoptimized={display.primaryImageSrc.includes('?')}
                  style={{ objectFit: 'cover' }}
                  // VOW photos are served behind a 6h signed proxy URL; once the
                  // signature ages out the request 403s. Fall back rather than
                  // render a broken image.
                  onError={(e) => applyPhotoFallback(e.currentTarget as HTMLImageElement)}
                />
              </Box>
            )
          ) : (
            <Box
              aria-hidden
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0.3,
              }}
            >
              <svg viewBox="0 0 48 48" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.4">
                <rect x="4" y="16" width="40" height="28" rx="2" />
                <path d="M4 22L24 8L44 22" />
                <rect x="18" y="30" width="12" height="14" />
              </svg>
            </Box>
          )}

          {/* "New" chip (MLS, listed within 7 days) — top-left */}
          {/* "New" chip (MLS, listed within 7 days) — top-left, solo. */}
          {display.isRental && (
            <Chip
              label={t('properties.card.forRentChip', { defaultValue: 'For Rent' })}
              size="small"
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
                zIndex: 2,
              }}
            />
          )}
          {/* Off-market status chip — same top-left slot and treatment as the
              freshness chip (mutually exclusive: freshness is suppressed on
              off-market cards in toDisplay). Status itself is not VOW data. */}
          {display.isMls && offMarketStatus ? (
            <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 2 }}>
              <Chip
                label={
                  offMarketStatus === 'Closed'
                    ? t('properties.card.offMarketChip.sold', { defaultValue: 'Sold' })
                    : offMarketStatus === 'Pending'
                      ? t('properties.card.offMarketChip.pending', { defaultValue: 'Pending' })
                      : offMarketStatus === 'Expired'
                        ? t('properties.card.offMarketChip.expired', { defaultValue: 'Expired' })
                        : t('properties.card.offMarketChip.terminated', { defaultValue: 'Terminated' })
                }
                size="small"
                sx={{
                  backgroundColor:
                    offMarketStatus === 'Closed'
                      ? theme.semanticColors.interactivePrimary
                      : alpha(theme.rawSemanticColors.surfaceCard, 0.92),
                  color:
                    offMarketStatus === 'Closed'
                      ? theme.semanticColors.surfaceCard
                      : theme.semanticColors.textPrimary,
                  border:
                    offMarketStatus === 'Closed'
                      ? 'none'
                      : `1px solid ${theme.semanticColors.borderSubtle}`,
                  backdropFilter: glassBlur.subtle,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              />
            </Box>
          ) : null}
          {/* VOW blur veil — cosmetic layer over DECOY pixels only (the server
              never ships real VOW bytes to non-registrants). Sits below the
              status chip (zIndex 2) so the chip stays readable. */}
          {vowGated ? (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                backdropFilter: 'blur(14px)',
                backgroundColor: alpha(theme.rawSemanticColors.surfaceCard, 0.25),
              }}
            />
          ) : null}
          {display.isMls && display.freshnessLabel ? (
            <Box sx={{ position: 'absolute', top: 10, left: display.isRental ? 82 : 10 }}>
              <Chip
                label={
                  display.freshnessLabel === 'New'
                    ? t('properties.card.newChip', { defaultValue: 'New' })
                    : t('properties.card.newlyListedChip', { defaultValue: 'Newly Listed' })
                }
                size="small"
                sx={{
                  backgroundColor: theme.semanticColors.interactivePrimary,
                  color: theme.semanticColors.surfaceCard,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              />
            </Box>
          ) : null}

          {/* "Under your rent" chip, top-right. Shown only when the listing
              has no price cut, so the card carries one money tag at a time. */}
          {underRent ? (
            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
              <Chip
                label={t('properties.card.underRentChip', { defaultValue: 'Under your rent' })}
                size="small"
                sx={{
                  backgroundColor: theme.semanticColors.statusSuccess,
                  color: theme.semanticColors.surfaceCard,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              />
            </Box>
          ) : null}

          {/* Open-house chip — bottom-right of the photo, above the
              photo-count badge. Theme-aware plate; readable in both modes. */}
          {display.isMls && display.nextOpenHouseLabel ? (
            <Box sx={{ position: 'absolute', bottom: 38, right: 10 }}>
              <Chip
                label={display.nextOpenHouseLabel}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.rawSemanticColors.surfaceCard, 0.92),
                  backdropFilter: 'blur(4px)',
                  color: theme.semanticColors.textPrimary,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  border: `1px solid ${theme.semanticColors.borderSubtle}`,
                }}
              />
            </Box>
          ) : null}

          {/* Remarks-only match disclosure — top-left, subtle. Signals that
              the pill matched via public_remarks (not an MLS-tagged field),
              so buyers don't expect a hard-fact filter to behave like one. */}
          {display.isMls && display.matchDisclosureLabel ? (
            <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
              <Chip
                label={display.matchDisclosureLabel}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.rawSemanticColors.surfaceCard, 0.85),
                  backdropFilter: 'blur(4px)',
                  color: theme.semanticColors.textSecondary,
                  fontWeight: 500,
                  fontSize: 10,
                  height: 20,
                  borderStyle: 'dashed',
                  borderWidth: 1,
                  borderColor: theme.semanticColors.borderSubtle,
                }}
              />
            </Box>
          ) : null}

          {/* Photo count badge (MLS) — bottom-right */}
          {display.isMls && (display.photosCount ?? 0) > 1 ? (
            <Box
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: 'rgba(0,0,0,0.55)',
                color: '#fff',
                fontSize: 11,
                fontWeight: 500,
                lineHeight: 1.4,
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              <svg
                viewBox="0 0 16 16"
                width="12"
                height="12"
                fill="currentColor"
                aria-hidden
              >
                <path d="M5.5 2.5L4.4 4H2a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V5a1 1 0 0 0-1-1h-2.4L10.5 2.5h-5zM8 12a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7zm0-1.5A2 2 0 1 0 8 6.5a2 2 0 0 0 0 4z" />
              </svg>
              {display.photosCount}
            </Box>
          ) : null}

          {/* Status badge (curated only) */}
          {curatedListing ? (
            <Box sx={{ position: 'absolute', bottom: 10, left: 10 }}>
              <Chip
                label={t((STATUS_I18N[curatedListing.status] ?? STATUS_FALLBACK).key, {
                  defaultValue: (STATUS_I18N[curatedListing.status] ?? STATUS_FALLBACK).defaultValue,
                })}
                size="small"
                sx={{
                  backgroundColor: alpha(statusColor, 0.14),
                  color: statusColor,
                  border: `1px solid ${alpha(statusColor, 0.28)}`,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  backdropFilter: glassBlur.subtle,
                }}
              />
            </Box>
          ) : null}

          {/* Price reduction badge (curated only) */}
          {curatedListing?.previousPrice ? (
            <Box sx={{ position: 'absolute', bottom: 10, left: curatedListing.status === 'active' ? 76 : 10 }}>
              <Chip
                label={t('properties.card.priceReduced', { defaultValue: 'Price reduced' })}
                size="small"
                sx={{
                  backgroundColor: alpha(theme.rawSemanticColors.statusWarning, 0.14),
                  color: theme.semanticColors.statusWarning,
                  border: `1px solid ${alpha(theme.rawSemanticColors.statusWarning, 0.28)}`,
                  fontWeight: 600,
                  fontSize: 11,
                  height: 22,
                  backdropFilter: glassBlur.subtle,
                }}
              />
            </Box>
          ) : null}
        </Box>

        {/* Card body */}
        <Stack spacing={0} sx={{ pt: `${theme.layout.cardPadding.md}px`, px: bodyPx, pb: 0, gap: `${theme.layout.form.labelGap / 2}px` }}>
          {/* Price row: asking price, then $/sqft on the right. On a narrow
              2-column phone card the $/sqft wraps under the price instead of
              colliding with it. */}
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            spacing={0}
            sx={{ gap: `${theme.layout.form.labelGap}px`, flexWrap: 'wrap', rowGap: 0 }}
          >
            <Stack direction="row" alignItems="baseline" spacing={0} sx={{ gap: `${theme.layout.form.labelGap}px`, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: theme.typography.h4,
                  fontWeight: 600,
                  fontVariantNumeric: 'tabular-nums',
                  color: theme.semanticColors.textPrimary,
                  lineHeight: 1.2,
                  ...(vowGated ? { filter: 'blur(8px)', userSelect: 'none' } : null),
                }}
                aria-hidden={vowGated || undefined}
              >
                {display.price != null ? formatPrice(display.price) : '—'}
                {lease && display.price != null ? t('rentalsSearch.card.perMonth', { defaultValue: '/mo' }) : null}
              </Typography>
              {curatedListing?.previousPrice ? (
                <Typography
                  sx={{
                    fontSize: theme.typography.bodySm,
                    color: theme.semanticColors.textMuted,
                    textDecoration: 'line-through',
                  }}
                >
                  {formatPrice(curatedListing.previousPrice)}
                </Typography>
              ) : null}
            </Stack>
            {display.pricePerSqft != null ? (
              <Typography
                aria-hidden={vowGated || undefined}
                sx={{
                  fontSize: 12.5,
                  color: theme.semanticColors.textMuted,
                  fontVariantNumeric: 'tabular-nums',
                  whiteSpace: 'nowrap',
                  ...(vowGated ? { filter: 'blur(6px)', userSelect: 'none' } : null),
                }}
              >
                {formatPrice(display.pricePerSqft)}/{t('properties.card.sqft', { defaultValue: 'sqft' })}
              </Typography>
            ) : null}
          </Stack>

          {/* Monthly mortgage estimate */}
          {monthly != null && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                ~{formatMonthly(monthly)}/mo
              </Typography>
              <Tooltip
                title={
                  <span>
                    Estimated monthly payment based on MLS® list price with {mortgageAssumptions.downPct}% down, {mortgageAssumptions.amortYears}-year amortization, {mortgageAssumptions.rate}% annual rate. This is an estimate only, not financial advice. Rate, down payment, and final price may differ. Verify with a mortgage professional.
                  </span>
                }
                placement="top"
                arrow
                enterTouchDelay={0}
                leaveTouchDelay={4000}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    border: '1px solid',
                    borderColor: 'text.disabled',
                    color: 'text.disabled',
                    fontSize: 9,
                    fontWeight: 600,
                    cursor: 'help',
                    lineHeight: 1,
                    flexShrink: 0,
                  }}
                >
                  i
                </Box>
              </Tooltip>
            </Stack>
          )}

          {presalePills}
          {renderSpecs(specParts)}

          {/* Address / title */}
          <Typography
            title={vowGated ? undefined : displayTitle}
            aria-hidden={vowGated || undefined}
            sx={{
              ...(vowGated ? { filter: 'blur(6px)', userSelect: 'none' } : null),
              fontSize: theme.typography.bodyMd,
              fontWeight: 500,
              color: theme.semanticColors.textPrimary,
              lineHeight: 1.3,
              // The street address alone is clamped so a long one does not
              // push a card taller than its siblings. Attribution is never
              // clamped (see below).
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            {displayTitle}
          </Typography>

          {/* Type · place */}
          {subtitleLine ? (
            <Typography
              title={subtitleLine}
              sx={{
                fontSize: { xs: 13, md: theme.typography.bodySm },
                color: theme.semanticColors.textSecondary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                minWidth: 0,
              }}
            >
              {subtitleLine}
            </Typography>
          ) : null}
        </Stack>
      </Link>

      {/* Price-cut chip, outside the link because it holds the VOW gate
          button. Asking-price history only; never a day count (no days on
          market on a card). */}
      {priceCutContent ? (
        <Box sx={{ px: bodyPx, pt: `${theme.layout.form.labelGap / 2}px` }}>
          <Box
            component="span"
            data-testid="price-cut-chip"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              minHeight: 24,
              px: 1,
              borderRadius: '6px',
              backgroundColor: semanticRoleColors.successSoft,
              color: theme.semanticColors.textPrimary,
              fontSize: 12.5,
              fontWeight: 500,
              lineHeight: 1.3,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {priceCutContent}
          </Box>
        </Box>
      ) : null}

      {/* BCRES attribution (§8.12.03): brokerage and MLS® number, always in
          full. It wraps onto more lines rather than clamping, so a long
          brokerage name makes the card taller instead of hiding who listed
          the home. It sits outside the listing anchor and above the actions,
          and the action row is pinned to the card bottom, so grid rows stay
          aligned however many lines this takes. There is no "Data from
          BCRES" line here, which is the treatment Attribution.tsx gives DDF
          rows; IDX and DDF cards read the same. */}
      {display.isMls && (display.attribution || display.mlsId) ? (
        <Box
          data-testid="card-attribution"
          sx={{
            mx: bodyPx,
            mt: `${theme.layout.form.labelGap}px`,
            pt: `${theme.layout.form.labelGap}px`,
            borderTop: `1px solid ${theme.semanticColors.borderSubtle}`,
            fontSize: 11.5,
            lineHeight: 1.45,
            color: theme.semanticColors.textMuted,
            overflowWrap: 'anywhere',
            minWidth: 0,
          }}
        >
          {display.attribution ? (
            <Box component="span" sx={{ display: 'block', fontStyle: 'italic' }}>
              {/* Brokerage is rendered as data, never interpolated: an
                  uninitialised i18n instance (SSR, tests) returns the raw
                  template and the board-required name would vanish. */}
              {t('properties.card.listedByPrefix', { defaultValue: 'Listed by' })} {display.attribution}
            </Box>
          ) : null}
          {display.mlsId ? (
            <Box component="span" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
              MLS® {display.mlsId}
            </Box>
          ) : null}
        </Box>
      ) : offMarketStatus ? (
        /* Off-market cards whose brokerage is absent (gated decoys, or records
           not yet re-ingested) keep the attribution slot's height so they
           align with cards that show a brokerage. */
        <Box
          aria-hidden
          sx={{
            mx: bodyPx,
            mt: `${theme.layout.form.labelGap}px`,
            pt: `${theme.layout.form.labelGap}px`,
            borderTop: `1px solid ${theme.semanticColors.borderSubtle}`,
            fontSize: 11.5,
            lineHeight: 1.45,
          }}
        >
          {' '}
        </Box>
      ) : null}

      {/* CTA rows live OUTSIDE the Link: interactive controls nested inside
          an anchor are a WCAG nested-interactive violation and break
          keyboard navigation. Same horizontal padding as the content Stack
          so the card reads as one column. */}
      <Box sx={{ px: bodyPx, pt: `${theme.layout.form.labelGap}px`, pb: `${theme.layout.cardPadding.md}px`, mt: 'auto' }}>
          {/* Off-market cards keep the SAME slot anatomy as active cards
              (primary row + secondary row) so grid heights align. Behavior
              is gated, not layout: decoys route every action to the VOW
              modal via onViewDetails. */}
          <>
          {/* CTA row: View / Ask / surface CTA. */}

          <Stack
            direction="row"
            spacing={1}
            sx={{
              pt: `${theme.layout.form.labelGap}px`,
              alignItems: 'center',
            }}
          >
            {/* Surface CTA (Remind me / Watch the price / …). Sits in this row
                because the row is already outside the Link; a control nested
                inside the anchor is a nested-interactive violation AND is
                swallowed by ListingModalLinks' capture-phase handler. */}
            {showCardCta && cardCta && (
              cardCta.href ? (
                <Button
                  component={Link}
                  href={cardCta.href}
                  variant="outlined"
                  onClick={(e) => { e.stopPropagation(); cardCta.onActivate?.(); }}
                  sx={{
                    flexGrow: 0,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: `${theme.layout.button.sm.fontSize}px`,
                    px: `${theme.layout.button.sm.paddingX}px`,
                    py: `${theme.layout.button.sm.paddingY}px`,
                    minHeight: `${theme.layout.button.sm.minHeight}px`,
                    borderRadius: `${theme.layout.radius.md}px`,
                  }}
                >
                  {cardCta.label}
                </Button>
              ) : (
                <Button
                  variant={isInquiryCta ? 'contained' : 'outlined'}
                  disabled={cardCta.onActivate == null}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    cardCta.onActivate?.({
                      listingKey: props.source === 'mls' ? props.listing.listingKey : (props.listing.slug ?? ''),
                      listingSlug: props.source === 'mls'
                        ? (props.listing.listingSlug ?? null)
                        : props.listing.slug,
                      title: display.title,
                      price: display.price,
                      mlsId: display.mlsId ?? null,
                      source: props.source,
                    });
                  }}
                  sx={{
                    flexGrow: isInquiryCta ? 1 : 0,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: `${theme.layout.button.sm.fontSize}px`,
                    px: `${theme.layout.button.sm.paddingX}px`,
                    py: `${theme.layout.button.sm.paddingY}px`,
                    minHeight: `${theme.layout.button.sm.minHeight}px`,
                    borderRadius: `${theme.layout.radius.md}px`,
                  }}
                >
                  {cardCta.label}
                </Button>
              )
            )}

            {/* "Ask" inquiry button — shown when onInquire is wired (e.g. liked list) */}
            {onInquire && !offMarketStatus && (
              <Button
                variant="contained"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onInquire(); }}
                sx={{
                  flexGrow: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: `${theme.layout.button.sm.fontSize}px`,
                  px: `${theme.layout.button.sm.paddingX}px`,
                  py: `${theme.layout.button.sm.paddingY}px`,
                  minHeight: `${theme.layout.button.sm.minHeight}px`,
                  borderRadius: `${theme.layout.radius.md}px`,
                }}
              >
                {t('properties.card.askAbout', { defaultValue: 'Inquire' })}
              </Button>
            )}
            {/* "View" button — label shortened when Ask is also present */}
            {viewAction.kind === 'callback' ? (
              <Button
                variant={viewVariant}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onViewDetails?.();
                }}
                sx={viewDetailsSx}
              >
                {viewDetailsLabel}
              </Button>
            ) : viewAction.kind === 'link' ? (
              <Button
                component={Link}
                href={viewAction.href}
                // Same marker as the card-body anchor, so ListingModalLinks
                // upgrades this CTA into the in-place modal too.
                data-listing-key={props.source === 'mls' ? props.listing.listingKey : undefined}
                prefetch={cardPrefetch}
                {...warmOnIntent}
                target={viewAction.target}
                rel={viewAction.rel}
                variant={viewVariant}
                // Stop here: the map panel wraps each card in its own onClick,
                // so letting this bubble navigated twice (new tab + in-page).
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  trackCtaClick('View Property', locationTag, display.href);
                }}
                sx={viewDetailsSx}
              >
                {viewDetailsLabel}
              </Button>
            ) : (
              <Button variant={viewVariant} disabled sx={viewDetailsSx}>
                {viewDetailsLabel}
              </Button>
            )}
          </Stack>


          {/* Secondary row — same height on every card. Sold/Expired/
              Terminated: one full-width ghost CTA (unlock for decoys, a
              label variant for registrants). Pending + active: Watch and
              Compare, plus Suggest for staff with client_workspace. Three
              labeled startIcon buttons overflow a grid card, so that case
              stacks icon above label. */}
          {agentActions ? (
          agentRow
          ) : offMarketStatus && offMarketStatus !== 'Pending' ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ pt: `${theme.layout.form.labelGap}px`, alignItems: 'center' }}
          >
            <Button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onViewDetails?.(); }}
              sx={{
                flexGrow: 1,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: `${theme.layout.button.sm.fontSize}px`,
                px: `${theme.layout.button.sm.paddingX}px`,
                py: `${theme.layout.button.sm.paddingY}px`,
                minHeight: `${theme.layout.button.sm.minHeight}px`,
                border: `1px solid ${theme.semanticColors.borderSubtle}`,
                borderRadius: `${theme.layout.radius.md}px`,
                color: theme.semanticColors.textSecondary,
                transition: `color 150ms, border-color 150ms`,
                '&:hover': {
                  color: theme.semanticColors.interactivePrimary,
                  borderColor: theme.semanticColors.interactivePrimary,
                },
              }}
            >
              {vowGated
                ? t('properties.card.vowUnlock', { defaultValue: 'See sold data' })
                : t('properties.card.soldData', { defaultValue: 'Sold data' })}
            </Button>
          </Stack>
          ) : (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              pt: `${theme.layout.form.labelGap}px`,
              alignItems: 'stretch',
              '& > *': { minWidth: 0 },
            }}
          >
            {!hideLikeButton && (
              <Button
                onClick={handleWishlist}
                aria-label={
                  isWishlisted
                    ? t('properties.card.removeFromLiked', { defaultValue: 'Remove from watchlist' })
                    : t('properties.card.likeThisHome', { defaultValue: 'Watch' })
                }
                startIcon={<ThumbsUpIcon filled={isWishlisted} />}
                sx={cardActionButtonSx(theme, {
                  stacked: stackSecondaryActions,
                  tone: isWishlisted ? 'active' : 'plain',
                })}
              >
                {t('properties.card.watch', { defaultValue: 'Watch' })}
              </Button>
            )}
            {showSuggest && (
              <SuggestToClientControl
                variant={stackSecondaryActions ? 'stacked' : 'button'}
                listing={{ listingKey: agentListingKey, title: display.title }}
              />
            )}
            {/* Compare toggle */}
            <Button
              onClick={handleCompare}
              aria-label={isInCompare ? 'Remove from compare' : 'Add to compare'}
              aria-pressed={isInCompare}
              disabled={!canAddToCompare}
              startIcon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="12" y1="3" x2="12" y2="21" />
                  <polyline points="6 7 12 3 18 7" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="12" x2="6" y2="20" />
                  <line x1="20" y1="12" x2="18" y2="20" />
                </svg>
              }
              sx={cardActionButtonSx(theme, {
                stacked: stackSecondaryActions,
                tone: isInCompare ? 'selected' : 'plain',
              })}
            >
              {t('properties.card.compare', { defaultValue: 'Compare' })}
            </Button>
          </Stack>
          )}
          </>

      </Box>
    </Paper>
    <WatchedSnackbar open={snackOpen} onClose={closeSnack} />
    <RemovedFromListSnackbar open={removedOpen} onClose={closeRemoved} onUndo={undoRemove} />
    </>
  );
};
